import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ApiError, get, post } from '../api';
import { Combobox } from '../components/Combobox';
import { Field, Problems } from '../components/Field';
import { toast } from '../components/toast';
import { addDays, currentLocale, dayLabel, mondayOf, todayLocal, weekdayLabel } from '../dates';
import { hoursLabel, minutesFrom } from '../duration';
import { describe } from '../errors';
import { useLoad, useMedia } from '../hooks';
import {
  CLOSED,
  FLAG,
  TONES,
  isOpen,
  needsAttention,
  statusOf,
  type RosterEntry,
  type ScheduleVisit as Visit,
} from '../visitStatus';
import {
  ActiveFilters,
  SHOW_LABEL,
  ScheduleFilters,
  type Show,
} from '../components/ScheduleFilters';
import { WeekStrip, weekRange } from '../components/WeekStrip';
import { ClockIcon, PersonIcon, PlaceIcon } from '../components/NavIcons';
import { VisitPanel } from './VisitPanel';

type View = 'day' | 'week' | 'month';
const VIEWS: { id: View; label: string; key: string }[] = [
  { id: 'day', label: 'Day', key: 'd' },
  { id: 'week', label: 'Week', key: 'w' },
  { id: 'month', label: 'Month', key: 'm' },
];
const SHOWS = Object.keys(SHOW_LABEL) as Show[];

/** What an empty filter means, said plainly rather than as a generic "nothing here". */
const CLEAR: Record<Exclude<Show, 'all'>, string> = {
  attention: 'Nothing needs attention',
  no_lead: 'Every visit has a lead',
  time_off: 'Nobody is booked in their time off',
  unconfirmed: 'Every visit is confirmed',
  clash: 'No clashes',
};
const PERIOD: Record<View, string> = {
  day: 'on this day',
  week: 'this week',
  month: 'this month',
};

const monthOf = (d: string) => d.slice(0, 7);
const monthTitle = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString(currentLocale(), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
/** The first of the month before or after the one holding `d`. */
const shiftMonth = (d: string, n: number) => {
  const t = new Date(`${d.slice(0, 7)}-01T00:00:00Z`);
  t.setUTCMonth(t.getUTCMonth() + n);
  return t.toISOString().slice(0, 10);
};
/** The last day of the month holding `d`. */
const lastOfMonth = (d: string) => addDays(shiftMonth(d, 1), -1);
const daysFrom = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/** Flags the office acts on from the calendar. "Changed" is for contractors, who see it in the portal. */
const CARD_FLAGS = (v: Visit) =>
  v.flags.filter((f) => f !== 'closed' && f !== 'changed' && FLAG[f]);

/** How long a visit runs, from its start and end times ("09:00", "11:30"). */
const lengthOf = (v: Visit) => {
  const at = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  return at(v.end_time) - at(v.start_time);
};

function VisitCard(props: { v: Visit; onOpen(id: number): void }) {
  const { v } = props;
  const status = statusOf(v);
  const closed = v.state === 'inactive';
  // Who is on it, lead first; a visit with nobody says so through its "No lead" flag instead.
  const team = v.lead_name ? [v.lead_name, ...v.assist_names] : v.team;
  return (
    <li className={`${closed ? 'visit closed' : 'visit'} tone-${status.tone}`}>
      {/* The customer is the card's title; when, where and who sit under it, each by its icon. */}
      <button
        type="button"
        className="link visit-name"
        data-visit={v.id}
        onClick={() => props.onOpen(v.id)}
      >
        {v.customer_name}
      </button>
      <div className="visit-meta">
        <span className="visit-when">
          <ClockIcon />
          <span className="time">
            {v.start_time}–{v.end_time}
          </span>
          <span className="visit-length">{hoursLabel(lengthOf(v))}</span>
        </span>
        <span>
          <PlaceIcon />
          <span className="sr-only">Where: </span>
          {v.locality}
        </span>
        {closed ? (
          <span>{CLOSED[v.reason] ?? v.reason}</span>
        ) : (
          team.length > 0 && (
            <span>
              <PersonIcon />
              <span className="sr-only">{v.lead_name ? 'Lead: ' : 'Team: '}</span>
              {v.lead_name ? (
                <>
                  <span className="visit-lead">{v.lead_name}</span>
                  {v.assist_names.length > 0 && <>, {v.assist_names.join(', ')}</>}
                </>
              ) : (
                team.join(', ')
              )}
            </span>
          )
        )}
      </div>
      {CARD_FLAGS(v).length > 0 && (
        <div className="flags">
          {CARD_FLAGS(v).map((f) => (
            <span key={f} className={`pill ${FLAG[f]!.tone}`}>
              {FLAG[f]!.label}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A column heading for a day: its weekday over its date number, with the full label kept for screen readers. */
function DayHeading(props: { d: string }) {
  return (
    <span className="day-heading">
      <span aria-hidden="true">
        <span className="weekday">{weekdayLabel(props.d)}</span>
        <span className="daynum">{Number(props.d.slice(8))}</span>
      </span>
      <span className="sr-only">{dayLabel(props.d)}</span>
    </span>
  );
}

/** Typing into a field, or a dialog in front: the page's own keys stand aside. */
const typing = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  return (
    e.defaultPrevented ||
    e.altKey ||
    e.ctrlKey ||
    e.metaKey ||
    !!t?.closest('input, select, textarea, [contenteditable="true"], [role="dialog"]') ||
    !!document.querySelector('[role="dialog"]')
  );
};

export function Schedule() {
  const [params, setParams] = useSearchParams();
  const view: View = VIEWS.some((x) => x.id === params.get('view'))
    ? (params.get('view') as View)
    : 'week';
  const show: Show = SHOWS.includes(params.get('show') as Show)
    ? (params.get('show') as Show)
    : 'all';
  // In the address, like the view, so a filtered week can be bookmarked or sent to someone.
  // Any number of contractors, comma-separated; none means everyone's visits.
  const contractor = (params.get('who') ?? '')
    .split(',')
    .map(Number)
    .filter((n) => n > 0);
  const client = Number(params.get('client')) || 0;
  // Cancelled and missed visits are out of the way unless asked for.
  const showCancelled = params.get('cancelled') === '1';
  // `week` is the older name for the date being looked at; keep it working for saved links.
  const anchor = params.get('date') ?? params.get('week') ?? todayLocal();
  const weekly = view === 'week';
  const first =
    view === 'day' ? anchor : weekly ? mondayOf(anchor) : mondayOf(`${monthOf(anchor)}-01`);
  // A month runs from the Monday on or before the 1st to the Sunday on or after its last day.
  const count =
    view === 'day'
      ? 1
      : weekly
        ? 7
        : daysFrom(first, addDays(mondayOf(lastOfMonth(anchor)), 6)) + 1;
  const days = Array.from({ length: count }, (_, i) => addDays(first, i));
  const from = first;
  const to = days[days.length - 1]!;
  const openId = Number(params.get('visit')) || 0;
  const [adding, setAdding] = useState(false);
  const [tick, setTick] = useState(0);
  // A phone: a strip of the week's days does the paging. Month keeps its own grid, which is already the overview.
  const narrow = useMedia('(max-width: 720px)');
  const strip = narrow && view !== 'month';
  const monday = mondayOf(anchor);
  // Day view's strip counts every day of its week, so on a phone it loads the whole week around it.
  const loadFrom = strip ? (view === 'day' ? monday : from) : from;
  const loadTo = strip && view === 'day' ? addDays(monday, 6) : to;

  const feed = useLoad(
    () =>
      get<{ today: string; items: Visit[]; contractors?: RosterEntry[] }>(
        `/api/schedule?from=${loadFrom}&to=${loadTo}`,
      ),
    [loadFrom, loadTo, tick],
  );
  const link = (next: {
    view?: View;
    date?: string;
    show?: Show;
    who?: number[];
    client?: number;
    cancelled?: boolean;
    visit?: number;
  }) => {
    const v = next.view ?? view;
    const s = next.show ?? show;
    const d = 'date' in next ? next.date : anchor;
    const w = next.who ?? contractor;
    const c = next.client ?? client;
    const x = next.cancelled ?? showCancelled;
    const o = 'visit' in next ? next.visit : openId;
    const out: Record<string, string> = {};
    if (v !== 'week') out.view = v;
    if (s !== 'all') out.show = s;
    if (d && d !== todayLocal()) out.date = d;
    if (w.length) out.who = w.join(',');
    if (c) out.client = String(c);
    if (x) out.cancelled = '1';
    if (o) out.visit = String(o);
    setParams(out);
  };
  const openVisit = (id: number | null) => link({ visit: id ?? 0 });
  const step = (dir: 1 | -1) =>
    link({
      date:
        view === 'day'
          ? addDays(anchor, dir)
          : weekly
            ? addDays(from, 7 * dir)
            : shiftMonth(anchor, dir),
    });
  const unit = view === 'day' ? 'day' : weekly ? 'week' : 'month';
  const backToNow = () => link({ date: undefined });

  // Keys for the office's everyday moves: ← → step, T comes back to now, D W C M switch views.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (typing(e)) return;
      const k = e.key.toLowerCase();
      const target = VIEWS.find((x) => x.key === k);
      if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
      else if (k === 't') backToNow();
      else if (target) link({ view: target.id });
      else return;
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const title =
    view === 'day'
      ? dayLabel(from)
      : weekly
        ? `${dayLabel(from)} – ${dayLabel(days[6]!)}`
        : monthTitle(anchor);
  const today = feed.data?.today ?? todayLocal();
  const loaded = feed.data?.items ?? [];
  const all = loaded.filter((v) => v.date >= from && v.date <= to);

  // Everyone the office could give work to, from the feed; anyone on a visit but no longer
  // active is added from the visits, so their work still has a row.
  const roster = new Map<number, RosterEntry>((feed.data?.contractors ?? []).map((r) => [r.id, r]));
  for (const v of all) {
    v.contractor_ids.forEach((id, i) => {
      if (!roster.has(id)) roster.set(id, { id, name: v.team[i] ?? '', time_off: [] });
    });
  }
  const contractors = new Map([...roster.values()].map((r) => [r.id, r.name]));
  const inTeam = (v: Visit) =>
    contractor.length === 0 || v.contractor_ids.some((id) => contractor.includes(id));
  // Client choices come from the range itself, so every option leads to something.
  const clients = new Map<number, string>();
  for (const v of all) clients.set(v.client_id, v.client_name);
  const cancelledTone = (v: Visit) => statusOf(v).tone === 'closed';
  const hidden = (v: Visit) => !showCancelled && cancelledTone(v);
  const matches = (v: Visit) =>
    !hidden(v) &&
    inTeam(v) &&
    (client === 0 || v.client_id === client) &&
    (show === 'all' ||
      (show === 'attention' ? needsAttention(v) : isOpen(v) && v.flags.includes(show)));
  const shown = all.filter(matches);
  // How many each "show" choice would find, within the chosen contractor and client.
  const scoped = all.filter((v) => inTeam(v) && (client === 0 || v.client_id === client));
  const counts: Record<Exclude<Show, 'all'>, number> = {
    attention: scoped.filter(needsAttention).length,
    no_lead: scoped.filter((v) => isOpen(v) && v.flags.includes('no_lead')).length,
    time_off: scoped.filter((v) => isOpen(v) && v.flags.includes('time_off')).length,
    unconfirmed: scoped.filter((v) => isOpen(v) && v.flags.includes('unconfirmed')).length,
    clash: scoped.filter((v) => isOpen(v) && v.flags.includes('clash')).length,
  };
  const attentionCount = counts.attention;
  const cancelledCount = scoped.filter(cancelledTone).length;
  const hiddenCount = showCancelled ? 0 : cancelledCount;
  // How many visits each contractor's tick would add, under the show and client choices.
  const contractorCounts = new Map<number, number>();
  for (const v of all) {
    if (hidden(v)) continue;
    if (client !== 0 && v.client_id !== client) continue;
    if (
      show !== 'all' &&
      !(show === 'attention' ? needsAttention(v) : isOpen(v) && v.flags.includes(show))
    )
      continue;
    for (const id of v.contractor_ids)
      contractorCounts.set(id, (contractorCounts.get(id) ?? 0) + 1);
  }
  const unassignedCount = shown.filter((v) => isOpen(v) && v.contractor_ids.length === 0).length;
  const perDay = (d: string) => shown.filter((v) => v.date === d);
  // Day and week views always show the grid itself (empty and all), rather than
  // replacing it with a placeholder; only month collapses to one when there is nothing at all.
  // Only once this range has loaded: the last range's data says nothing about this one.
  const nothingAtAll = !feed.loading && feed.data && all.length === 0 && view === 'month';

  // The one-off visit starts on the day being looked at, or today when today is in view.
  const addDate =
    view === 'day'
      ? anchor
      : today >= from && today <= to
        ? today
        : view === 'month'
          ? `${monthOf(anchor)}-01`
          : from;

  const opened = all.find((v) => v.id === openId);

  // The pager: in the toolbar on a wide screen; on a phone only Month uses it, stuck to the top.
  const pager = (
    <div className="pager week-nav">
      <button
        type="button"
        className="quiet step"
        aria-keyshortcuts="ArrowLeft"
        title={`Previous ${unit} (←)`}
        onClick={() => step(-1)}
      >
        ‹<span className="sr-only"> Previous {unit}</span>
      </button>
      <strong aria-live="polite">{title}</strong>
      <button
        type="button"
        className="quiet step"
        aria-keyshortcuts="ArrowRight"
        title={`Next ${unit} (→)`}
        onClick={() => step(1)}
      >
        ›<span className="sr-only"> Next {unit}</span>
      </button>
      <button
        type="button"
        className="quiet now"
        aria-keyshortcuts="T"
        title="Back to now (T)"
        onClick={backToNow}
      >
        {view === 'day' ? 'Today' : view === 'month' ? 'This month' : 'This week'}
      </button>
    </div>
  );

  const tally = (d: string) => {
    const theirs = loaded.filter((v) => v.date === d && matches(v));
    return { open: theirs.filter(isOpen).length, flagged: theirs.filter(needsAttention).length };
  };
  // The colour key: under the summary on a wide screen, kept in Filters on a phone.
  const legend = (heading?: string) => (
    <>
      {heading && <span className="legend-heading">{heading}</span>}
      <ul className="legend" aria-label="Colour key">
        {TONES.map((t) => (
          <li key={t.tone}>
            <span className={`dot tone-${t.tone}`} aria-hidden="true" /> {t.label}
          </li>
        ))}
      </ul>
    </>
  );
  const nowInView =
    view === 'day' ? anchor === today : today >= monday && today <= addDays(monday, 6);

  return (
    <div className="schedule-page">
      <div className="row">
        <h1>Schedule</h1>
        {narrow ? (
          // On a phone, one way to add, then which kind: two buttons side by side crowd the title.
          <Menu.Root>
            <Menu.Trigger className="button add-trigger">+ Add</Menu.Trigger>
            <Menu.Portal>
              <Menu.Content className="account-menu add-menu" align="end" sideOffset={6}>
                <Menu.Item onSelect={() => setAdding(true)}>
                  <strong>One-off visit</strong>
                  <span>An extra visit on an existing assignment</span>
                </Menu.Item>
                <Menu.Item asChild>
                  <Link to="/assignments/new">
                    <strong>Assignment</strong>
                    <span>Regular work for a customer</span>
                  </Link>
                </Menu.Item>
              </Menu.Content>
            </Menu.Portal>
          </Menu.Root>
        ) : (
          <div className="head-actions">
            <Link className="button quiet-button" to="/assignments/new">
              + Add assignment
            </Link>
            <button type="button" onClick={() => setAdding(true)}>
              + Add visit
            </button>
          </div>
        )}
      </div>

      <div className="toolbar">
        <div className="tabs" role="group" aria-label="View">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={v.id === view ? 'tab on' : 'tab'}
              aria-pressed={v.id === view}
              aria-keyshortcuts={v.key.toUpperCase()}
              title={`${v.label} (${v.key.toUpperCase()})`}
              onClick={() => link({ view: v.id })}
            >
              {v.label}
            </button>
          ))}
        </div>
        {!narrow && pager}
        <div className="quick-filters">
          <button
            type="button"
            className={show === 'attention' ? 'chip on' : 'chip'}
            aria-pressed={show === 'attention'}
            onClick={() => link({ show: show === 'attention' ? 'all' : 'attention' })}
          >
            Needs attention{' '}
            {counts.attention > 0 && <span className="count">{counts.attention}</span>}
          </button>
          <ScheduleFilters
            show={show}
            counts={counts}
            onShow={(s) => link({ show: s })}
            contractor={contractor}
            contractors={contractors}
            contractorCounts={contractorCounts}
            onContractor={(ids) => link({ who: ids })}
            client={client}
            clients={clients}
            onClient={(id) => link({ client: id })}
            cancelled={showCancelled}
            cancelledCount={cancelledCount}
            onCancelled={(on) => link({ cancelled: on })}
            onClear={() =>
              link({
                show: show === 'attention' ? 'attention' : 'all',
                who: [],
                client: 0,
                cancelled: false,
              })
            }
            extra={narrow && <div className="filter-group">{legend('Colours')}</div>}
          />
        </div>
      </div>

      <ActiveFilters
        items={[
          ...(show !== 'all' && show !== 'attention'
            ? [{ key: 'show', label: SHOW_LABEL[show], clear: () => link({ show: 'all' }) }]
            : []),
          ...contractor.map((id) => ({
            key: `contractor-${id}`,
            label: contractors.get(id) || 'Contractor',
            clear: () => link({ who: contractor.filter((c) => c !== id) }),
          })),
          ...(client
            ? [
                {
                  key: 'client',
                  label: clients.get(client) || 'Client',
                  clear: () => link({ client: 0 }),
                },
              ]
            : []),
          ...(showCancelled
            ? [
                {
                  key: 'cancelled',
                  label: 'Showing cancelled',
                  clear: () => link({ cancelled: false }),
                },
              ]
            : []),
        ]}
        onClearAll={() => link({ show: 'all', who: [], client: 0, cancelled: false })}
      />

      {narrow && view === 'month' && pager}
      {strip && (
        <WeekStrip
          monday={monday}
          today={today}
          title={view === 'day' ? dayLabel(anchor) : weekRange(monday)}
          stepLabel="week"
          // Day view picks its day from the strip; Week lists every day below, so it shows only
          // the week's first and last dates.
          days={
            view === 'day'
              ? { selected: anchor, tally, onPick: (d) => link({ date: d }) }
              : undefined
          }
          onStep={(dir) => (view === 'day' ? link({ date: addDays(anchor, 7 * dir) }) : step(dir))}
          onToday={nowInView ? undefined : backToNow}
          nowLabel={view === 'day' ? 'Today' : 'This week'}
        />
      )}

      {feed.data && !nothingAtAll && (
        <div className="summary">
          <p className="muted" aria-live="polite">
            {feed.loading ? (
              'Loading visits…'
            ) : (
              <>
                {plural(shown.filter(isOpen).length, 'visit')}
                {/* On a phone the Needs attention chip already says how many. */}
                {!narrow && attentionCount > 0 && ` · ${attentionCount} need attention`}
                {unassignedCount > 0 && ` · ${unassignedCount} unassigned`}
                {hiddenCount > 0 && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="link"
                      onClick={() => link({ cancelled: true })}
                    >
                      {plural(hiddenCount, 'cancelled visit')} hidden
                    </button>
                  </>
                )}
              </>
            )}
          </p>
          {!narrow && legend()}
        </div>
      )}

      {feed.error && (
        <p role="alert" className="error">
          {feed.error}
        </p>
      )}
      {nothingAtAll && (
        <div className="empty-state">
          <h2>No visits in this {view === 'month' ? 'month' : 'week'}</h2>
          <p>
            Visits are made ahead of time from assignments, so an empty week usually means no
            assignment covers it yet.
          </p>
          <div className="pager">
            <Link className="button" to="/assignments/new">
              Add an assignment
            </Link>
            <button type="button" className="quiet" onClick={() => setAdding(true)}>
              Add a one-off visit
            </button>
          </div>
        </div>
      )}
      {/* Only when the filter hid something: a range with no visits already reads as free days. */}
      {!feed.loading &&
        feed.data &&
        !nothingAtAll &&
        show !== 'all' &&
        shown.length === 0 &&
        all.length > 0 && (
          <p className="muted filter-clear">
            {CLEAR[show]} {PERIOD[view]}.
          </p>
        )}

      {/* While a range loads, the grid stays in place but dimmed under a spinner: an empty
          grid would read as a free week. The very first load shows the grid's shape instead. */}
      <div className={`schedule-body${feed.loading ? ' is-loading' : ''}`} aria-busy={feed.loading}>
        {feed.loading && (
          <div className="schedule-loading" role="status">
            <span className="spinner" aria-hidden="true" />
            Loading visits…
          </div>
        )}
        {feed.loading && !feed.data && <div className="skeleton schedule-skeleton" />}

        {feed.data && !nothingAtAll && (
          <div
            className={view === 'month' ? 'week month' : view === 'day' ? 'week one-day' : 'week'}
          >
            {view === 'month' &&
              days.slice(0, 7).map((d) => (
                <span key={d} className="month-weekday" aria-hidden="true">
                  {weekdayLabel(d)}
                </span>
              ))}
            {days.map((d) => {
              const visits = perDay(d);
              const open = visits.filter(isOpen);
              const flagged = visits.filter(needsAttention).length;
              if (view === 'month') {
                const outside = monthOf(d) !== monthOf(anchor);
                return (
                  <section
                    key={d}
                    className={`day compact${d === today ? ' today' : ''}${outside ? ' outside' : ''}`}
                    aria-label={dayLabel(d)}
                  >
                    <h2>
                      <button
                        type="button"
                        className="link month-day"
                        onClick={() => link({ view: 'day', date: d })}
                      >
                        {Number(d.slice(8))}
                        <span className="sr-only">
                          {' '}
                          {dayLabel(d)}
                          {visits.length > 0 && `, ${plural(visits.length, 'visit')}`}
                          {flagged > 0 && `, ${flagged} to check`}
                        </span>
                      </button>
                      {visits.length > 0 && (
                        <span className={flagged > 0 ? 'pill bad' : 'pill'} aria-hidden="true">
                          {visits.length}
                        </span>
                      )}
                    </h2>
                    {/* On a phone the day is one tap; its visits show as dots in their colours. */}
                    {visits.length > 0 && (
                      <span className="month-dots" aria-hidden="true">
                        {visits.slice(0, 4).map((v) => (
                          <span key={v.id} className={`dot tone-${statusOf(v).tone}`} />
                        ))}
                      </span>
                    )}
                    <ul>
                      {visits.slice(0, 3).map((v) => (
                        <li
                          key={v.id}
                          className={`${v.state === 'inactive' ? 'visit closed' : 'visit'} tone-${statusOf(v).tone}`}
                        >
                          <button
                            type="button"
                            className="link"
                            data-visit={v.id}
                            title={statusOf(v).label}
                            onClick={() => openVisit(v.id)}
                          >
                            {v.start_time} {v.customer_name}
                            <span className="sr-only"> ({statusOf(v).label})</span>
                          </button>
                        </li>
                      ))}
                      {visits.length > 3 && (
                        <li className="muted">
                          <button
                            type="button"
                            className="link"
                            onClick={() => link({ view: 'day', date: d })}
                          >
                            +{visits.length - 3} more
                          </button>
                        </li>
                      )}
                    </ul>
                  </section>
                );
              }
              return (
                <section
                  key={d}
                  className={`day${d === today ? ' today' : ''}${visits.length === 0 ? ' free' : ''}`}
                  aria-label={dayLabel(d)}
                >
                  <h2>
                    <DayHeading d={d} />
                    {d === today && <span className="pill fresh">Today</span>}
                    {open.length > 0 && (
                      <span className="day-count">{plural(open.length, 'visit')}</span>
                    )}
                    {flagged > 0 && <span className="pill bad">{flagged} to check</span>}
                  </h2>
                  {visits.length === 0 && <p className="empty">Free</p>}
                  {visits.length > 0 && (
                    <ul>
                      {visits.map((v) => (
                        <VisitCard key={v.id} v={v} onOpen={openVisit} />
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {openId > 0 && (
        <VisitPanel
          id={openId}
          item={opened}
          roster={feed.data?.contractors}
          sameDay={opened ? all.filter((v) => v.date === opened.date) : []}
          onClose={() => openVisit(null)}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}

      {adding && (
        <AddVisit
          defaultDate={addDate}
          onClose={() => setAdding(false)}
          onAdded={(id, date) => {
            setAdding(false);
            toast('Visit added.');
            // Stay on the calendar: show the day it landed on, with the new visit open.
            link({ date, visit: id });
            setTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

interface AssignmentRow {
  id: number;
  customer_name: string;
  client_name: string;
}

/** A single extra visit on an existing assignment, outside its pattern. */
function AddVisit(props: {
  defaultDate: string;
  onClose(): void;
  onAdded(id: number, date: string): void;
}) {
  const list = useLoad(
    () => get<{ items: AssignmentRow[] }>('/api/assignments?status=scheduled&limit=200'),
    [],
  );
  const [assignment, setAssignment] = useState(0);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(props.defaultDate);
  const [time, setTime] = useState('09:00');
  const [hours, setHours] = useState('2');
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    if (assignment === 0) {
      setProblems(['Choose which customer this visit is for.']);
      return;
    }
    setBusy(true);
    try {
      const r = await post<{ id: number }>(`/api/assignments/${assignment}/visits`, {
        date,
        time,
        minutes: minutesFrom(hours),
      });
      props.onAdded(r.id, date);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
      if (!(err instanceof ApiError)) setProblems(['Could not reach the server']);
    } finally {
      setBusy(false);
    }
  }

  const items = Array.isArray(list.data?.items) ? list.data!.items : [];
  const label = (a: AssignmentRow) =>
    `${a.customer_name}${a.client_name ? ` (${a.client_name})` : ''}`;
  const term = search.trim().toLowerCase();
  const picked = items.find((a) => a.id === assignment);
  const found = items
    .filter(
      (a) => !term || (picked && label(picked) === search) || label(a).toLowerCase().includes(term),
    )
    .slice(0, 50);
  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && !busy && props.onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="backdrop">
          <RadixDialog.Content
            className="dialog"
            aria-describedby="add-visit-d"
            // Escape first closes an open customer list, then the dialog.
            onEscapeKeyDown={(e) => {
              if ((e.target as HTMLElement | null)?.getAttribute('aria-expanded') === 'true') {
                e.preventDefault();
              }
            }}
          >
            <form className="stack" onSubmit={submit} noValidate>
              <RadixDialog.Title asChild>
                <h2>Add a one-off visit</h2>
              </RadixDialog.Title>
              <RadixDialog.Description id="add-visit-d" className="muted">
                An extra visit on an existing assignment, priced and staffed like its other visits.
              </RadixDialog.Description>
              <Problems problems={problems} title="Not added." />
              <Field label="Customer">
                <Combobox
                  label="Customer"
                  text={search}
                  onText={(t) => {
                    setSearch(t);
                    if (assignment) setAssignment(0);
                  }}
                  options={found.map((a) => ({ id: a.id, label: label(a) }))}
                  onPick={(o) => {
                    setAssignment(o.id);
                    setSearch(o.label);
                  }}
                  placeholder={list.loading ? 'Loading customers…' : 'Search customers…'}
                  busy={list.loading}
                  hasValue={assignment > 0}
                  onClear={() => {
                    setAssignment(0);
                    setSearch('');
                  }}
                />
              </Field>
              <div className="row">
                <Field label="Date" error={fields.date}>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field label="Time" error={fields.time}>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                </Field>
                <Field label="Length (hours)" error={fields.minutes}>
                  <input
                    inputMode="decimal"
                    className="short"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                  />
                </Field>
              </div>
              <div className="row end">
                <RadixDialog.Close asChild>
                  <button type="button" className="quiet">
                    Cancel
                  </button>
                </RadixDialog.Close>
                <button type="submit" disabled={busy}>
                  {busy ? 'Adding…' : 'Add visit'}
                </button>
              </div>
            </form>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
